import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, Pencil, Trash2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { formatIraqiPhone } from '@/lib/utils';
import { Spinner } from '@/components/Spinner';

interface StaffMember {
  id: string;
  name: string;
  phone: string;
  isOwner: boolean;
  isActive: boolean;
  createdAt: string;
}

interface StaffForm {
  name: string;
  phone: string;
}

export default function StaffPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [staffToDelete, setStaffToDelete] = useState<StaffMember | null>(null);
  const [form, setForm] = useState<StaffForm>({ name: '', phone: '' });
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery<{ staff: StaffMember[] }>({
    queryKey: ['owner', 'staff'],
    queryFn: () => api.get('/owner/staff'),
  });

  const createMutation = useMutation({
    mutationFn: (data: StaffForm) =>
      api.post<{ staff: StaffMember }>('/owner/staff', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner', 'staff'] });
      closeDialog();
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ staffId, data }: { staffId: string; data: Partial<StaffForm> & { isActive?: boolean } }) =>
      api.put<{ staff: StaffMember }>(`/owner/staff/${staffId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner', 'staff'] });
      closeDialog();
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (staffId: string) =>
      api.delete<{ success: boolean }>(`/owner/staff/${staffId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owner', 'staff'] });
      setDeleteDialogOpen(false);
      setStaffToDelete(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingStaff(null);
    setForm({ name: '', phone: '' });
    setError('');
  };

  const openAddDialog = () => {
    setEditingStaff(null);
    setForm({ name: '', phone: '' });
    setError('');
    setDialogOpen(true);
  };

  const openEditDialog = (staff: StaffMember) => {
    setEditingStaff(staff);
    setForm({ name: staff.name, phone: staff.phone });
    setError('');
    setDialogOpen(true);
  };

  const handleToggleActive = (staff: StaffMember) => {
    updateMutation.mutate({
      staffId: staff.id,
      data: { isActive: !staff.isActive },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.phone.trim()) {
      setError('الاسم ورقم الهاتف مطلوبان');
      return;
    }

    if (editingStaff) {
      const updateData: Partial<StaffForm> = {};
      if (form.name !== editingStaff.name) updateData.name = form.name;
      if (form.phone !== editingStaff.phone) updateData.phone = form.phone;

      if (Object.keys(updateData).length === 0) {
        closeDialog();
        return;
      }

      updateMutation.mutate({ staffId: editingStaff.id, data: updateData });
    } else {
      createMutation.mutate(form);
    }
  };

  const staff = data?.staff || [];
  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">الموظفون</h1>
          <p className="text-gray-500 text-sm mt-1">إدارة موظفي المغسلة</p>
        </div>
        <button
          onClick={openAddDialog}
          className="flex items-center gap-1.5 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700"
        >
          <Plus className="w-4 h-4" />
          إضافة موظف
        </button>
      </div>

      {/* Staff List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : staff.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-card p-8 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">لا يوجد موظفون مسجلون</p>
          <p className="text-sm text-gray-400 mt-1">أضف موظفين للسماح لهم بتسجيل الدخول إلى تطبيق المغسلة</p>
        </div>
      ) : (
        <div className="space-y-3">
          {staff.map((member) => (
            <div
              key={member.id}
              className={`bg-white rounded-2xl shadow-card p-4 transition-colors ${
                !member.isActive ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-sm font-bold bg-primary-100 text-primary-700">
                    {member.name.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{member.name}</span>
                      {!member.isActive && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          غير نشط
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5" dir="ltr">
                      {formatIraqiPhone(member.phone)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(member)}
                      className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                        member.isActive
                          ? 'bg-red-50 text-red-600 hover:bg-red-100'
                          : 'bg-green-50 text-green-600 hover:bg-green-100'
                      }`}
                    >
                      {member.isActive ? 'تعطيل' : 'تفعيل'}
                    </button>
                    <button
                      onClick={() => openEditDialog(member)}
                      className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setStaffToDelete(member);
                        setError('');
                        setDeleteDialogOpen(true);
                      }}
                      className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={closeDialog} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" dir="rtl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900">
                {editingStaff ? 'تعديل بيانات الموظف' : 'إضافة موظف جديد'}
              </h2>
              <button onClick={closeDialog} className="rounded-lg p-1.5 hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الاسم <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="اسم الموظف"
                  className="w-full rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رقم الهاتف <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="07xxxxxxxxx"
                  dir="ltr"
                  className="w-full rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
              </div>

              <p className="text-xs text-gray-400">
                سيستخدم الموظف رمز التحقق (OTP) لتسجيل الدخول
              </p>

              {error && (
                <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeDialog}
                  className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={isMutating}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 transition disabled:opacity-50"
                >
                  {isMutating ? (
                    <Spinner size="sm" />
                  ) : editingStaff ? (
                    <Pencil className="w-4 h-4" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {editingStaff ? 'حفظ التغييرات' : 'إضافة الموظف'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && staffToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDeleteDialogOpen(false)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" dir="rtl">
            <h2 className="text-lg font-bold text-gray-900 mb-2">حذف الموظف</h2>
            <p className="text-sm text-gray-600 mb-5">
              هل أنت متأكد من حذف <span className="font-semibold">{staffToDelete.name}</span>؟
              لا يمكن التراجع عن هذا الإجراء.
            </p>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 mb-4">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setStaffToDelete(null);
                  setError('');
                }}
                className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                إلغاء
              </button>
              <button
                onClick={() => deleteMutation.mutate(staffToDelete.id)}
                disabled={deleteMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
